import React, { FC } from 'react';
import styled, { css } from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import { A11y, Autoplay, Pagination, SwiperOptions } from 'swiper';
import { Spacer } from './Spacer';
import { Typography } from './Typography';
import { StatsFragment } from '../types/graphqlTypes';
import { ButtonRenderer } from './buttonRenderer/ButtonRenderer';
import { StyledCardCarouselWrapperSwiper } from './CardCarouselWrapper';

interface StatsProps extends StatsFragment {
  narrow?: boolean;
}

interface StyledStatisticsProps {
  numberOfStats: number;
}

const StyledStat = styled(Spacer)`
  ${({ theme: { space, colors, media, maxMedia }, narrow }) => css`
    display: flex;
    flex-direction: column;
    position: relative;
    padding: 0 ${space.md};

    span {
      display: block;
      text-wrap: balance;
    }

    @media (max-width: ${maxMedia.sm}) {
      text-align: center;

      a,
      button {
        margin-left: auto;
        margin-right: auto;
      }

      &:before {
        display: none;
      }
    }

    // Table < styles

    @media (max-width: ${maxMedia.lg}) {
      &:nth-child(3):before,
      &:nth-child(4):before {
        content: '';
        width: 100%;
        height: 1px;
        background: ${colors.grey3.default};
        top: -${space.lg};
        left: 0;
        position: absolute;
      }

      &:nth-child(2):after,
      &:nth-child(4):after {
        content: '';
        width: 1px;
        height: 100%;
        background: ${colors.grey3.default};
        top: 0;
        left: -${space.md};
        position: absolute;
      }
    }

    // Desktop > styles

    @media (min-width: ${media.lg}) {
      padding: 0 ${space.lg};

      & + &:before {
        content: '';
        width: 1px;
        height: 100%;
        background: ${colors.grey3.default};
        top: 0;
        left: -${space.md};
        position: absolute;
      }
    }

    // Narrow

    ${narrow &&
    css`
      @media (min-width: ${media.lg}) {
        &:first-child {
          padding-left: 0;
        }
        &:last-child {
          padding-right: 0;
        }
      }
    `}
  `};
`;

const StyledStatText = styled(Spacer)`
  flex-grow: 1;
`;

const StyledDesktopStatistics = styled.ul<StyledStatisticsProps>`
  ${({ theme: { space, colors, media, maxMedia }, numberOfStats }) => css`
    display: grid;
    grid-template-columns: ${numberOfStats >= 4 ? 'repeat(2, 1fr)' : 'repeat(1, 1fr)'};
    grid-column-gap: ${space.lg};
    grid-row-gap: ${space.xl};

    // Desktop > styles

    @media (min-width: ${media.lg}) {
      grid-template-columns: repeat(${numberOfStats}, 1fr);
      display: ${numberOfStats >= 4 ? 'grid' : 'flex'};
      justify-content: center;
      gap: ${space.lg};
    }

    // Hide on mobile

    @media (max-width: ${maxMedia.sm}) {
      display: none;
    }

    // Adjust stat if less than 4

    ${numberOfStats < 4 &&
    css`
      ${StyledStat} {
        @media (max-width: ${maxMedia.lg}) {
          text-align: center;
          &:after {
            display: none;
          }

          &:nth-child(2):before {
            content: '';
            width: 100%;
            height: 1px;
            background: ${colors.grey3.default};
            top: -${space.lg};
            left: 0;
            position: absolute;
          }

          &:last-child {
            border-bottom: solid 1px ${colors.grey3.default};
            padding-bottom: ${space.lg};
          }
        }
      }
    `}
  `};
`;

const StyledMobileStatistics = styled.ul<StyledStatisticsProps>`
  ${({ theme: { space, colors, media, maxMedia }, numberOfStats }) => css`
    // show on mobile
    display: none;

    @media (max-width: ${maxMedia.sm}) {
      display: block;
    }
  `};
`;

export const Stats: FC<StatsProps> = ({ stats, narrow }) => {
  if (!stats || stats?.length < 1) {
    return <></>;
  }

  const opts: SwiperOptions = {
    watchOverflow: true,
    updateOnImagesReady: true,
    loop: true,
    autoplay: {
      delay: 3000,
    },
    speed: 1000,
    pagination: {
      type: 'progressbar',
    },
    preventInteractionOnTransition: true,
    spaceBetween: 16,
    slidesPerView: 1,
  };

  return (
    <>
      <StyledDesktopStatistics numberOfStats={stats.length}>
        {stats.map(({ headline, text, button }, index) => (
          <StyledStat forwardedAs="li" key={index} responsive spacing="quarter" narrow={narrow}>
            <StyledStatText as="p" responsive spacing="quarter">
              <Typography component="strong" variant="displayLarge" color="brand">
                {headline}
              </Typography>
              <span>{text}</span>
            </StyledStatText>
            {button && <ButtonRenderer button={button} arrowLink />}
          </StyledStat>
        ))}
      </StyledDesktopStatistics>
      <StyledMobileStatistics>
        <StyledCardCarouselWrapperSwiper modules={[Pagination, A11y, Autoplay]} {...opts} disableOffset>
          {stats.map(({ headline, text, button }, index) => (
            <SwiperSlide>
              <StyledStat forwardedAs="li" key={index} responsive spacing="quarter" narrow={narrow}>
                <StyledStatText as="p" responsive spacing="quarter">
                  <Typography component="strong" variant="displayLarge" color="brand">
                    {headline}
                  </Typography>
                  <span>{text}</span>
                </StyledStatText>
                {button && <ButtonRenderer button={button} arrowLink />}
              </StyledStat>
            </SwiperSlide>
          ))}
        </StyledCardCarouselWrapperSwiper>
      </StyledMobileStatistics>
    </>
  );
};
